import { Button } from '@/components/commons';
import { AyumiService } from '@/services/ayumi';
import { ENDPOINT } from '@/services/endpoint';
import { NOTIFY_STATUS, notificationMessage } from '@/utils';
import { PlusOutlined } from '@ant-design/icons';
import { Alert, Collapse } from 'antd';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { IntroVideoItemCard } from './IntroVideoItemCard';

/**
 * 課題一覧の紹介動画（HIBINOE-677）。
 *
 * アプリのあゆみ詳細ページ（課題一覧）上部のカルーセルに、上から順に並ぶ。
 * 本数は可変（上限なし）。1 本目が最初に真ん中に来て、左右はループする。
 *
 * 導入ボトムシートの紹介動画（`IntroVideoSection`、1 本・縦長）とは別物。
 * 同じ動画を使う場合も、それぞれに入稿する。
 */
export const IntroVideoItemsSection: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const {
    data: items = [],
    refetch,
    isFetching,
    isSuccess,
    isError,
  } = useQuery(
    [ENDPOINT.AYUMI.GET_INTRO_VIDEOS],
    () => AyumiService.getIntroVideoItems(),
    { select: (res) => res?.data ?? [] },
  );

  const { mutate: reorder, isLoading: isReordering } = useMutation(
    (introVideoIds: number[]) =>
      AyumiService.reorderIntroVideoItems({ introVideoIds }),
    {
      onSuccess: () => refetch(),
      onError: () => {
        // 他の人が追加・削除していると 400 になる。最新の一覧を取り直して並べ直してもらう
        notificationMessage({
          message:
            '並び替えに失敗しました。最新の一覧を読み込み直したので、もう一度並べ替えてください。',
          type: NOTIFY_STATUS.ERROR,
        });
        refetch();
      },
    },
  );

  const handleMove = (index: number, direction: -1 | 1) => {
    const next = [...items];
    const target = index + direction;

    if (target < 0 || target >= next.length) return;

    [next[index], next[target]] = [next[target], next[index]];
    reorder(next.map((item) => item.id));
  };

  const statusLabel = isFetching
    ? '読み込み中…'
    : isError
      ? '読み込みに失敗しました'
      : items.length
        ? `${items.length} 本`
        : '未入稿（帯を表示しません）';

  return (
    <Collapse
      className='mb-4'
      activeKey={isOpen ? ['introVideos'] : []}
      onChange={(keys) => setIsOpen(keys.length > 0)}
      items={[
        {
          key: 'introVideos',
          label: (
            <span className='font-semibold'>
              課題一覧の紹介動画
              <span className='ml-2 font-normal text-sm text-gray-500'>
                {statusLabel}
              </span>
            </span>
          ),
          children: (
            <>
              <div className='text-sm text-gray-500 mb-3'>
                あゆみ詳細ページ（課題一覧）上部のカルーセルに、上から順に並びます。
                1 本目が最初に真ん中に表示されます。
                導入ボトムシートの紹介動画とは別物です。
                サムネイルは自動生成されたものを外して、デザイン済みの画像に差し替えられます。
              </div>

              {isSuccess ? (
                <div className='flex flex-col gap-4'>
                  {items.length === 0 && !isAdding && (
                    <p className='text-[13px] text-[#888]'>
                      紹介動画がまだありません。「動画を追加」から入稿してください。
                    </p>
                  )}

                  {items.map((item, index) => (
                    <IntroVideoItemCard
                      key={item.id}
                      item={item}
                      position={index + 1}
                      canMoveUp={index > 0 && !isReordering}
                      canMoveDown={index < items.length - 1 && !isReordering}
                      onMove={(direction) => handleMove(index, direction)}
                      onSaved={() => refetch()}
                    />
                  ))}

                  {isAdding && (
                    <IntroVideoItemCard
                      position={items.length + 1}
                      canMoveUp={false}
                      canMoveDown={false}
                      onMove={() => undefined}
                      onSaved={() => {
                        setIsAdding(false);
                        refetch();
                      }}
                      onCancelNew={() => setIsAdding(false)}
                    />
                  )}

                  {!isAdding && (
                    <div>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => setIsAdding(true)}
                      >
                        動画を追加
                      </Button>
                    </div>
                  )}
                </div>
              ) : isError ? (
                <Alert
                  type='error'
                  showIcon
                  message='紹介動画を読み込めませんでした。'
                  action={
                    <Button size='small' onClick={() => refetch()}>
                      再読み込み
                    </Button>
                  }
                />
              ) : (
                <div className='text-sm text-gray-500'>読み込み中…</div>
              )}
            </>
          ),
        },
      ]}
    />
  );
};
